import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import "./App.css";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaBars,
  FaTimes,
} from "react-icons/fa";

const Facilities = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const facilities = [
    {
      title: "NICU (Neonatal Intensive Care Unit)",
      desc: "Advanced Level III NICU for critically ill and premature newborns, with ventilator and life support systems.",
      img: "/ashtavinayakhospital_NICU_facility.png",
    },
    {
      title: "PICU (Pediatric Intensive Care Unit)",
      desc: "Dedicated intensive care unit for critically ill children, managed by pediatric intensivists and trained staff.",
      img: "/ashtavinayakhospital_PICU_facility.png",
    },
    {
      title: "Neurodevelopmental OPD & Rehabilitation",
      desc: "Focused care for developmental delays, neurological disorders, and rehabilitation therapies.",
      img: "/ashtavinayakhospital_neuro_facility.png",
    },
    {
      title: "Operation Theatres (OT)",
      desc: "Modern, sterile surgical suites for pediatric and neonatal procedures, including minimally invasive surgeries.",
      img: "/ashtavinayakhospital_OT_facility.png",
    },
    {
      title: "General to Suite Wards",
      desc: "Comfortable inpatient accommodations ranging from general wards to fully equipped private suites.",
      img: "/ashtavinayakhospital_general_ward.png",
    },
    {
      title: "OPD / Pediatric Clinic",
      desc: "Daily outpatient services offering consultations and follow-ups with pediatric specialists.",
      img: "/ashtavinayakhospital_OPD_facility.png",
    },
  ];

  // Scroll header effect
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "auto";
  }, [menuOpen]);

  return (
    <div className="about-page">
      {/* ✅ META TAGS */}
      <Helmet>
        <title>Facilities | Ashtavinayak Children's Hospital & Critical Care</title>
        <meta
          name="description"
          content="Explore the advanced medical facilities at Ashtavinayak Children's Hospital, including NICU, PICU, Operation Theatres, OPD, and rehabilitation care for children."
        />
        <meta
          name="keywords"
          content="NICU, PICU, pediatric hospital, children hospital Sambhajinagar, neonatal care, Ashtavinayak hospital facilities"
        />
        <link
          rel="canonical"
          href="https://ashtavinayakhospital.in/facilities"
        />
        {/* Open Graph for social media */}
        <meta property="og:title" content="Facilities | Ashtavinayak Children's Hospital" />
        <meta
          property="og:description"
          content="Take a look at our world-class pediatric care facilities, including NICU, PICU, and modern operation theatres."
        />
        <meta property="og:image" content="https://ashtavinayakhospital.in/og-facilities.jpg" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ashtavinayakhospital.in/facilities" />
      </Helmet>

      {/* ===== Header ===== */}
      <header
        className={`main-header ${isScrolled ? "scrolled" : ""}`}
        style={{
          zIndex: 5000,
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
        }}
      >
        <div className="navbar-container">
          <a href="/" className="navbar-logo">
            <img
              src="/logo.png"
              alt="Ashtavinayak Children's Hospital & Critical Care"
            />
          </a>

          <nav
            className={`nav-links ${menuOpen ? "active" : ""}`}
            style={{
              zIndex: 6000,
              position: menuOpen ? "fixed" : "",
            }}
          >
            <a href="/" onClick={() => setMenuOpen(false)}>
              Home
            </a>
            <a href="/about" onClick={() => setMenuOpen(false)}>
              About Us
            </a>
            <a href="/facilities" onClick={() => setMenuOpen(false)}>
              Facilities
            </a>
            <a href="/contact" onClick={() => setMenuOpen(false)}>
              Contact
            </a>
          </nav>

          <button
            className="menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle Menu"
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </header>

      {/* ===== Hero Section ===== */}
      <section className="about-hero">
        <div className="hero-overlay">
          <div className="about-hero-content">
            <h1>Our Facilities</h1>
            <p>
              Providing advanced pediatric and maternal healthcare facilities designed
              to ensure safety, comfort, and excellence for every patient.
            </p>
          </div>
        </div>
      </section>

      {/* ===== Facilities Grid ===== */}
      <section className="facilities-section">
        <h2 className="section-title">Our Facilities</h2>
        <div className="facilities-grid">
          {facilities.map((item, index) => (
            <div className="facility-card" key={index}>
              <img src={item.img} alt={item.title} className="facility-img" />
              <div className="facility-content">
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Sticky Call Button ===== */}
      <a href="tel:+918149492473" className="sticky-call-btn">
        <FaPhoneAlt className="call-icon" />
        <span>Call Us Now</span>
      </a>

      {/* ===== Footer ===== */}
      <footer className="footer-section">
        <div className="footer-container">
          <div className="footer-about">
            <img
              src="/logo.png"
              alt="Ashtavinayak Children's Hospital & Critical Care"
              className="footer-logo"
            />
            <p>
              Ashtavinayak Children's Hospital & Critical Care is dedicated to
              providing comprehensive medical care for children.
            </p>
          </div>

          <div className="footer-links">
            <h4>Quick Links</h4>
            <ul>
              <li><a href="/">Home</a></li>
              <li><a href="/about">About Us</a></li>
              <li><a href="/facilities">Facilities</a></li>
              <li><a href="/contact">Contact</a></li>
            </ul>
          </div>

          <div className="footer-contact">
            <h4>Contact Us</h4>
            <p>
              Ashtvinayak Hospital, KT Complex, beside Savitri Lawns, Sambhajinagar, India-431002
            </p>
            <p>
              <a href="tel:+918149492473" className="contact-link">
                <FaPhoneAlt /> +91 8149492473
              </a>
            </p>
            <p>
              <a
                href="mailto:ashtavinayakhospital@gmail.com"
                className="contact-link"
              >
                <FaEnvelope /> ashtavinayakhospital@gmail.com
              </a>
            </p>
          </div>
        </div>

        <div className="footer-bottom">
          <p>
            © {new Date().getFullYear()} Ashtavinayak Children's Hospital & Critical
            Care. All Rights Reserved.
            <br />
            <span className="developer-text">
              Designed & Developed by{" "}
              <a
                href="https://intelligicsoftware.in"
                target="_blank"
                rel="noopener noreferrer"
              >
                Intelligic Software Pvt. Ltd.
              </a>
            </span>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Facilities;
